import { Component, h, Prop, Element } from '@stencil/core';
import { parseJsonProp } from '../../../../utils/helpers/hydration';

/**
 * eSignature features component that displays key eSignature capabilities
 * using features-type-8 layout with feature cards.
 *
 * @product signnow
 * @source solutions
 * @category Features
 * @slot title - {optional}, <me-heading variant="3"> - Section title (can also use title prop)
 * @example
 *   <sn-esignature-features
 *     values='{"title": "Accelerate your document workflows...", "items": [{"title": "Boost paperwork", ...}]}'
 *   >
 *   </sn-esignature-features>
 */
@Component({
  tag: 'sn-esignature-features',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SnEsignatureFeatures {
  @Element() el!: HTMLElement;

  /**
   * Array of feature items to be displayed in cards.
   * Icons are hardcoded based on item index (icon-1 for first item, icon-2 for second, etc.).
   * @dynamic
   * @type {array}
   * @items.type {object}
   * @items.property {string} title - Feature card title
   * @items.property {string} description - Feature card description
   * @items.required title
   * @items.required description
   * @minItems 1
   * @maxItems 6
   */
  @Prop({ mutable: true }) items?:
    | Array<{
        title: string;
        description: string;
        svgIcon?: string;
      }>
    | string;

  /**
   * Section title
   * @dynamic
   * @type {string}
   */
  @Prop({ attribute: 'title' }) heading?: string;

  /**
   * HTML tag for the heading element (h1, h2, h3, h4, h5, h6)
   * @default "h2"
   */
  @Prop() headingTag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' = 'h2';

  /**
   * HTML tag for the item heading element (h1, h2, h3, h4, h5, h6)
   * @default "h3"
   */
  @Prop() itemHeadingTag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' = 'h3';

  /**
   * @dynamic
   * @type {object}
   * @property {string} title - Section title
   * @property {array} items - Feature items
   */
  @Prop({ mutable: true }) values?:
    | {
        title?: string;
        items?: Array<{
          title: string;
          description: string;
          svgIcon?: string;
        }>;
      }
    | string;

  componentWillLoad() {
    this.values = parseJsonProp(this.values);
    this.items = parseJsonProp(this.items, []);

    if (!this.items && this.values && typeof this.values !== 'string') {
      this.items = this.values.items;
    }
  }

  render() {
    // Ensure items is parsed and is an array
    if (!Array.isArray(this.items) || this.items.length === 0) {
      return null;
    }

    const heading = (this.values && typeof this.values !== 'string' ? this.values.title : undefined) || this.heading;
    const cardsCount = this.items.length;
    let cardsModifier = '';
    if (cardsCount === 3) cardsModifier = 'sn-esignature-features__cards--triple';
    if (cardsCount === 4) cardsModifier = 'sn-esignature-features__cards--quad';
    if (cardsCount === 6) cardsModifier = 'sn-esignature-features__cards--six';

    return (
      <me-section class="sn-esignature-features" yGap="2xl" yGapLg="5xl" role="region" aria-label="Features">
        <me-row direction="column" yGap="2xs" yGapLg="3xl">
          <me-column span="12">
            {heading && (
              <me-heading variant="3" align="center" tag={this.headingTag}>
                {heading}
              </me-heading>
            )}
          </me-column>
          <me-column 
            span="12" 
            class={`sn-esignature-features__cards${cardsModifier ? ' ' + cardsModifier : ''}`}
            itemScope 
            itemType="https://schema.org/ItemList"
          >
            {this.items.map((item, index) => {
              // Hardcode icon based on index (0-based) -> icon number (1-based)
              const iconNumber = (index % 6) + 1;
              const iconName = `https://marketing-static.signnow.com/347/images/me/esignature-features/icon-${iconNumber}.svg`;
              const iconClass = item.svgIcon ? item.svgIcon : '';

              return (
                <features-card-type-6
                  key={String(index)}
                  itemscope
                  itemtype="https://schema.org/ListItem"
                  itemprop="itemListElement"
                >
                  <meta itemprop="position" content={String(index + 1)} />
                  <me-icon slot="icon" name={iconName} class={iconClass}></me-icon>
                  <me-heading slot="heading" variant="5" tag={this.itemHeadingTag}>
                    {item.title}
                  </me-heading>
                  <me-text slot="description">{item.description}</me-text>
                </features-card-type-6>
              );
            })}
          </me-column>
        </me-row>
      </me-section>
    );
  }
}